import { type ReactNode } from 'react';

import { cn } from '@shared/lib/cn';

export interface BaseTileProps {
  label: ReactNode;
  onClick: () => void;
  hint?: ReactNode;
  active?: boolean;
  /** Dashed "+ Add …" look. */
  dashed?: boolean;
  /** Small corner action (e.g. ✎ rename). */
  cornerAction?: { label: string; icon: string; onClick: () => void } | undefined;
}

/** Large clickable tile (Services page: three per row, prototype `.svctile`). */
export function BaseTile({ label, onClick, hint, active, dashed, cornerAction }: BaseTileProps) {
  return (
    <div className="relative min-w-0">
      <button
        type="button"
        onClick={onClick}
        aria-pressed={active}
        className={cn(
          'flex w-full cursor-pointer flex-col items-center gap-1 rounded-app border px-3.5 py-5.5 text-center text-[13px] font-semibold',
          // One set of colours each: cn() doesn't resolve clashing classes.
          dashed
            ? 'border-dashed border-line-strong bg-transparent text-text-3 hover:border-text-3 hover:text-text-2'
            : active
              ? 'border-accent bg-accent-soft text-accent'
              : 'border-line-strong bg-surface-2 text-text hover:bg-surface-3',
        )}
      >
        {label}
        {hint && <span className="text-[11px] font-normal text-text-3">{hint}</span>}
      </button>
      {cornerAction && (
        <button
          type="button"
          onClick={cornerAction.onClick}
          aria-label={cornerAction.label}
          title={cornerAction.label}
          className="absolute top-1.5 right-1.5 flex size-6 cursor-pointer items-center justify-center rounded-full border border-line-strong bg-surface text-text-3 hover:bg-surface-3 hover:text-text"
        >
          <i className={cn(cornerAction.icon, 'text-[10.5px]')} aria-hidden />
        </button>
      )}
    </div>
  );
}
